import { useMemo, useState } from 'react';
import isEqual from 'react-fast-compare';

import {
  HStack,
  Heading,
  Input,
  NewButton,
  Paragraph,
  Text,
  Textarea,
  VStack,
  useToast,
} from '@eluve/components';

export interface ConversationStarter {
  id: string;
  title: string;
  description: string;
  content: string;
}

interface ConversationStartersProps {
  initialStarters: ConversationStarter[];
  onSave: (starters: ConversationStarter[]) => Promise<void>;
}

export const ConversationStarters = ({
  initialStarters,
  onSave,
}: ConversationStartersProps) => {
  const toast = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [starters, setStarters] =
    useState<ConversationStarter[]>(initialStarters);
  const [isSaving, setIsSaving] = useState(false);

  const hasChanges = useMemo(
    () => !isEqual(starters, initialStarters),
    [starters, initialStarters],
  );

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(starters);
      setIsEditing(false);
    } catch {
      toast.error('Failed to save conversation starters');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setStarters(initialStarters);
    setIsEditing(false);
  };

  const handleAddStarter = () => {
    const newStarter: ConversationStarter = {
      id: crypto.randomUUID(),
      title: '',
      description: '',
      content: '',
    };
    setStarters([...starters, newStarter]);
  };

  const handleRemoveStarter = (id: string) => {
    setStarters(starters.filter((s) => s.id !== id));
  };

  const handleUpdateStarter = (
    id: string,
    updates: Partial<ConversationStarter>,
  ) => {
    setStarters(starters.map((s) => (s.id === id ? { ...s, ...updates } : s)));
  };

  return (
    <div className="w-full rounded-lg border border-border-primary bg-bg-secondary p-6">
      <VStack gap={4} className="w-full">
        <HStack className="w-full items-start justify-between">
          <VStack gap={1}>
            <Heading size="md" weight="semibold">
              Conversation Starters
            </Heading>
            <Paragraph size="sm" color="secondary">
              Pre-configured prompts to quickly start conversations
            </Paragraph>
          </VStack>
          {!isEditing && (
            <NewButton
              text="Edit"
              type="outline"
              size="s"
              icon={{ name: 'Pencil', position: 'left' }}
              onClick={() => setIsEditing(true)}
            />
          )}
        </HStack>

        {isEditing ? (
          <VStack gap={3} className="w-full">
            {starters.map((starter, index) => (
              <div
                key={starter.id}
                className="w-full rounded border border-border-secondary p-4"
              >
                <VStack gap={3} className="w-full">
                  <HStack className="w-full justify-between">
                    <Text weight="semibold" size="sm">
                      Starter {index + 1}
                    </Text>
                    <NewButton
                      text="Remove"
                      type="ghost"
                      size="s"
                      icon={{ name: 'Trash', position: 'left' }}
                      onClick={() => handleRemoveStarter(starter.id)}
                    />
                  </HStack>
                  <VStack gap={2} className="w-full">
                    <Input
                      type="text"
                      placeholder="Title"
                      value={starter.title}
                      onChange={(e) =>
                        handleUpdateStarter(starter.id, {
                          title: e.target.value,
                        })
                      }
                    />
                    <Input
                      type="text"
                      placeholder="Description"
                      value={starter.description}
                      onChange={(e) =>
                        handleUpdateStarter(starter.id, {
                          description: e.target.value,
                        })
                      }
                    />
                    <Textarea
                      placeholder="Prompt text"
                      value={starter.content}
                      onChange={(e) =>
                        handleUpdateStarter(starter.id, {
                          content: e.target.value,
                        })
                      }
                      rows={3}
                    />
                  </VStack>
                </VStack>
              </div>
            ))}
            <NewButton
              text="Add starter"
              type="outline"
              size="s"
              icon={{ name: 'Plus', position: 'left' }}
              onClick={handleAddStarter}
            />
            <HStack gap={2} className="w-full justify-end">
              <NewButton
                text="Cancel"
                type="outline"
                size="s"
                onClick={handleCancel}
              />
              <NewButton
                text="Save"
                type="primary"
                size="s"
                onClick={handleSave}
                disabled={!hasChanges || isSaving}
                loading={isSaving}
              />
            </HStack>
          </VStack>
        ) : (
          <VStack gap={2} className="w-full">
            {starters.length > 0 ? (
              starters.map((starter, index) => (
                <div
                  key={starter.id}
                  className="w-full rounded border border-border-secondary p-3"
                >
                  <VStack gap={1}>
                    <Text weight="semibold" size="sm">
                      {starter.title || `Starter ${index + 1}`}
                    </Text>
                    {starter.description && (
                      <Paragraph size="sm" color="secondary">
                        {starter.description}
                      </Paragraph>
                    )}
                  </VStack>
                </div>
              ))
            ) : (
              <Paragraph size="sm" color="tertiary">
                No conversation starters configured
              </Paragraph>
            )}
          </VStack>
        )}
      </VStack>
    </div>
  );
};
